/**
 * FreeOrthogonalGradedDoubleCategoryExplainer — interactive exposition of
 * the free orthogonal graded double category 𝔻₀(𝒮,E,R).
 *
 * This card explains what 𝔻₀ is, why it matters for security, and what it
 * enables — using interactive diagrams rather than proof ledgers.
 *
 * The main interactive element is a mode-switchable square diagram that lets
 * the reader explore the three structural possibilities for a 2-cell in 𝔻₀:
 *   - Pure effect (production): authority boundaries are trivial
 *   - Pure demand (authority): effect boundaries are trivial
 *   - Mixed (absent): no constructor exists — this IS the security guarantee
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useId, useState } from "react";
import {
  COMPOSITIONALITY_PRINCIPLE_COLOR,
  GRAY,
  THEOREM_COLOR,
  WARN,
  withAlpha,
} from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ArrowDefs,
  CalloutBox,
  ExplainerCard,
  WorkedExample,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.REQUIRE.color;
const EFFECT_COLOR = COMPOSITIONALITY_PRINCIPLE_COLOR.REQUIRE.color;
const DEMAND_COLOR = THEOREM_COLOR.SEPARATE;
const BLOCKED_COLOR = WARN[700];

// ── Identity ─────────────────────────────────────────────────────────

const IDENTITY: ExplainerIdentity = {
  id: "explainer-free-orthogonal-graded-double-category",
  kind: "Categorical structure",
  title: "Free orthogonal graded double category",
  subtitle: "𝔻₀(𝒮,E,R) — the baseline where effects and authority never silently couple",
  accent: ACCENT,
};

// ── Cross-links ──────────────────────────────────────────────────────

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Scope · Effect · Coeffect", href: "#explainer-scope-effect-coeffect", accent: ACCENT },
  { label: "Double category", href: "#explainer-double-category" },
  { label: "Beck-Chevalley", href: "#explainer-beck-chevalley" },
  { label: "Graded monad", href: "#explainer-graded-monad" },
  { label: "ALWAYS (Al)", href: "#explainer-f-algebra", accent: ACCENT },
];

// ── Interactive diagram ──────────────────────────────────────────────

type CellMode = "effect" | "demand" | "mixed";

const CELL_MODES: { mode: CellMode; label: string }[] = [
  { mode: "effect", label: "Pure effect" },
  { mode: "demand", label: "Pure demand" },
  { mode: "mixed", label: "Mixed (absent)" },
];

function CellExplorer(): React.JSX.Element {
  const [mode, setMode] = useState<CellMode>("effect");
  const markerId = useId();

  const selectMode = useCallback(
    (m: CellMode): (() => void) =>
      () => {
        setMode(m);
      },
    [],
  );

  const isEffect = mode === "effect";
  const isDemand = mode === "demand";
  const isMixed = mode === "mixed";

  const hColor = isMixed || isEffect ? EFFECT_COLOR : GRAY[400];
  const vColor = isMixed || isDemand ? DEMAND_COLOR : GRAY[400];
  const hStroke = isMixed || isEffect ? 2.5 : 1.5;
  const vStroke = isMixed || isDemand ? 2.5 : 1.5;
  const hDash = isEffect ? undefined : isDemand ? "6 4" : undefined;
  const vDash = isDemand ? undefined : isEffect ? "6 4" : undefined;

  // Derived colors for the caption panel
  const captionFill = isMixed
    ? withAlpha(BLOCKED_COLOR, "0A")
    : isEffect
      ? withAlpha(EFFECT_COLOR, "0A")
      : withAlpha(DEMAND_COLOR, "0A");
  const captionStroke = isMixed
    ? withAlpha(BLOCKED_COLOR, "33")
    : isEffect
      ? withAlpha(EFFECT_COLOR, "33")
      : withAlpha(DEMAND_COLOR, "33");

  return (
    <div aria-label="Cell explorer: switch between pure effect, pure demand, and mixed modes">
      {/* Mode selector buttons */}
      <div
        role="group"
        aria-label="Cell mode selector"
        style={{
          display: "flex",
          gap: "0.5rem",
          justifyContent: "center",
          flexWrap: "wrap",
          marginBottom: "0.75rem",
        }}
      >
        {CELL_MODES.map((opt) => {
          const active = mode === opt.mode;
          const btnColor =
            opt.mode === "effect"
              ? EFFECT_COLOR
              : opt.mode === "demand"
                ? DEMAND_COLOR
                : BLOCKED_COLOR;
          return (
            <button
              key={opt.mode}
              type="button"
              onClick={selectMode(opt.mode)}
              aria-pressed={active}
              style={{
                padding: "0.35rem 0.75rem",
                borderRadius: "999px",
                border: `1.5px solid ${active ? btnColor : GRAY[300]}`,
                backgroundColor: active ? withAlpha(btnColor, "14") : "white",
                color: active ? btnColor : GRAY[600],
                fontWeight: active ? 700 : 500,
                fontSize: "0.8125rem",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* The square diagram */}
      <svg
        viewBox="0 0 400 260"
        role="img"
        aria-label={`2-cell in 𝔻₀: ${mode} mode — ${isMixed ? "no constructor exists" : mode === "effect" ? "pure effect square with trivial vertical grades" : "pure demand square with trivial horizontal grades"}`}
        style={{ display: "block", maxWidth: "100%", height: "auto" }}
      >
        <ArrowDefs id={markerId} color={hColor} />
        <ArrowDefs id={`${markerId}-v`} color={vColor} />

        {/* Corner labels */}
        <text x={50} y={48} textAnchor="middle" fontSize="15" fontWeight={700} fill={GRAY[900]}>
          A
        </text>
        <text x={350} y={48} textAnchor="middle" fontSize="15" fontWeight={700} fill={GRAY[900]}>
          B
        </text>
        <text x={50} y={178} textAnchor="middle" fontSize="15" fontWeight={700} fill={GRAY[900]}>
          C
        </text>
        <text x={350} y={178} textAnchor="middle" fontSize="15" fontWeight={700} fill={GRAY[900]}>
          D
        </text>

        {/* Top horizontal arrow */}
        <path
          d="M 72 44 L 326 44"
          fill="none"
          stroke={hColor}
          strokeWidth={hStroke}
          strokeDasharray={hDash}
          markerEnd={`url(#${markerId}-head)`}
        />
        <text x={200} y={34} textAnchor="middle" fontSize="11" fontWeight={600} fill={hColor}>
          {isEffect ? "effect grade e ∈ E" : isDemand ? "identity 1_E" : "effect e ≠ 1_E"}
        </text>

        {/* Bottom horizontal arrow */}
        <path
          d="M 72 174 L 326 174"
          fill="none"
          stroke={hColor}
          strokeWidth={hStroke}
          strokeDasharray={hDash}
          markerEnd={`url(#${markerId}-head)`}
        />
        <text x={200} y={192} textAnchor="middle" fontSize="10" fill={hColor}>
          {isEffect ? "effect grade e′ ∈ E" : isDemand ? "identity 1_E" : "effect e′ ≠ 1_E"}
        </text>

        {/* Left vertical arrow */}
        <path
          d="M 50 60 L 50 160"
          fill="none"
          stroke={vColor}
          strokeWidth={vStroke}
          strokeDasharray={vDash}
          markerEnd={`url(#${markerId}-v-head)`}
        />
        <text
          x={26}
          y={112}
          textAnchor="middle"
          fontSize="10"
          fontWeight={600}
          fill={vColor}
          transform="rotate(-90 26 112)"
        >
          {isDemand ? "demand r ∈ R" : isEffect ? "identity 1_R" : "demand r ≠ 1_R"}
        </text>

        {/* Right vertical arrow */}
        <path
          d="M 350 60 L 350 160"
          fill="none"
          stroke={vColor}
          strokeWidth={vStroke}
          strokeDasharray={vDash}
          markerEnd={`url(#${markerId}-v-head)`}
        />
        <text
          x={374}
          y={112}
          textAnchor="middle"
          fontSize="10"
          fontWeight={600}
          fill={vColor}
          transform="rotate(90 374 112)"
        >
          {isDemand ? "demand r′ ∈ R" : isEffect ? "identity 1_R" : "demand r′ ≠ 1_R"}
        </text>

        {/* Center content */}
        {isMixed ? (
          <>
            {/* Big X for absent */}
            <path
              d="M 140 75 L 260 155"
              stroke={BLOCKED_COLOR}
              strokeWidth={4}
              strokeLinecap="round"
              opacity={0.7}
            />
            <path
              d="M 260 75 L 140 155"
              stroke={BLOCKED_COLOR}
              strokeWidth={4}
              strokeLinecap="round"
              opacity={0.7}
            />
            <text
              x={200}
              y={122}
              textAnchor="middle"
              fontSize="28"
              fontWeight={900}
              fill={BLOCKED_COLOR}
            >
              ⊥
            </text>
          </>
        ) : (
          <>
            <text
              x={200}
              y={112}
              textAnchor="middle"
              fontSize="22"
              fontWeight={700}
              fill={isEffect ? EFFECT_COLOR : DEMAND_COLOR}
            >
              {isEffect ? "α" : "β"}
            </text>
            <text x={200} y={134} textAnchor="middle" fontSize="11" fill={GRAY[600]}>
              {isEffect ? "production cell" : "demand cell"}
            </text>
          </>
        )}

        {/* Explanation caption */}
        <rect
          x={40}
          y={200}
          width={320}
          height={48}
          rx={6}
          fill={captionFill}
          stroke={captionStroke}
        />
        <text x={200} y={220} textAnchor="middle" fontSize="11" fontWeight={600} fill={GRAY[900]}>
          {isEffect
            ? "✓ Vertical boundaries carry identity grade — pure production"
            : isDemand
              ? "✓ Horizontal boundaries carry identity grade — pure authority"
              : "✗ No constructor — implicit coupling is structurally inexpressible"}
        </text>
        <text x={200} y={237} textAnchor="middle" fontSize="10" fill={GRAY[600]}>
          {isEffect
            ? "The step produces effects without consuming non-trivial authority"
            : isDemand
              ? "The step consumes authority without producing non-trivial effects"
              : "Any legitimate crossing requires explicit bridge data outside 𝔻₀"}
        </text>
      </svg>
    </div>
  );
}

// ── Tiers ────────────────────────────────────────────────────────────

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      𝔻₀(𝒮,E,R) is the most conservative governed computation baseline: effects and authority
      demands live on independent axes, and there is no cell that silently couples the two. Any
      coupling requires explicit governance data — a bridge, capability grant, or theorem-specific
      witness.
    </p>
  ),
  intuition: (
    <>
      <p>
        Think of a building with two independent access systems. One system tracks{" "}
        <strong>what you do</strong> (effects): open a file, send a request, write a log. The other
        tracks <strong>what you need permission for</strong> (authority demands): hold a capability
        token, present a valid credential, satisfy a budget.
      </p>
      <p>
        In 𝔻₀, these two systems are completely separate. You can produce effects (turn the lights
        on) or consume authority (swipe your badge) — but there is no built-in operation that
        secretly turns &ldquo;knowing a light is on&rdquo; into &ldquo;having permission to enter a
        room.&rdquo; That would require a <em>bridge</em>: explicit governance data from outside the
        baseline.
      </p>
      <p>
        <strong>Free</strong> means the construction has no equations beyond the monoid laws — no
        hidden collapses. <strong>Orthogonal</strong> means the effect axis and the demand axis
        never interfere. <strong>Graded double category</strong> means effects compose horizontally,
        demands compose vertically, and 2-cells (squares) describe governed transitions where both
        dimensions interact — but only in the two pure forms that 𝔻₀ provides.
      </p>
      <p>
        The construction is <strong>initial</strong>: any concrete governed system that respects the
        same discipline is a quotient of 𝔻₀. Extensions add bridges; they never reveal hidden mixed
        cells that were &ldquo;already there.&rdquo;
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        <strong>Definition.</strong> Given a scope presentation 𝒮, an effect-grade monoid{" "}
        <MathInline>{tex`(E, \cdot_{E}, 1_{E})`}</MathInline>, and a demand-grade monoid{" "}
        <MathInline>{tex`(R, \cdot_{R}, 1_{R})`}</MathInline>, the free orthogonal graded double
        category 𝔻₀(𝒮,E,R) has:
      </p>
      <ul>
        <li>Objects from 𝒮.</li>
        <li>
          Horizontal morphisms <MathInline>{tex`A \xrightarrow{e} B`}</MathInline> carrying an
          effect grade <MathInline>{tex`e \in E`}</MathInline>.
        </li>
        <li>
          Vertical morphisms <MathInline>{tex`A \xrightarrow{r}_{\downarrow} C`}</MathInline>{" "}
          carrying a demand grade <MathInline>{tex`r \in R`}</MathInline>.
        </li>
        <li>
          2-cells (squares) of exactly two kinds:
          <ul>
            <li>
              <strong>Pure effect squares</strong> — vertical boundaries carry identity grade{" "}
              <MathInline>{tex`1_{R}`}</MathInline>; the cell witnesses effect-grade compatibility.
            </li>
            <li>
              <strong>Pure demand squares</strong> — horizontal boundaries carry identity grade{" "}
              <MathInline>{tex`1_{E}`}</MathInline>; the cell witnesses demand-grade compatibility.
            </li>
          </ul>
        </li>
      </ul>
      <p>
        <strong>Orthogonality theorem.</strong> No cell of 𝔻₀ simultaneously carries a non-identity
        effect boundary and a non-identity demand boundary. For any cell{" "}
        <MathInline>{tex`\alpha`}</MathInline> with boundary data{" "}
        <MathInline>{tex`(f, k;\, v_{1}, v_{2})`}</MathInline>, at least one pair has identity
        grade.
      </p>
      <p>
        <strong>Normal form.</strong> Every cell is uniquely classified as production (pure effect)
        or demand (pure authority). The classification is a coproduct decomposition with a
        retraction — inject the normal data and recover the original cell.
      </p>
      <p>
        <strong>Initiality.</strong> For any graded double category 𝒢 with interpretation data for
        the objects, effect and demand monoids, and both pure square forms, there is a unique strict
        graded double functor <MathInline>{tex`\mathbb{D}_{0} \to \mathcal{G}`}</MathInline>. The
        functor is never required to map a mixed cell, because 𝔻₀ has none.
      </p>
      <p>
        <strong>Factorization uniqueness.</strong> Any classifier that agrees with the
        production/demand labeling on the two generators is forced to agree everywhere — the
        two-constructor structure admits no alternative classification.
      </p>
      <CellExplorer />
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Why this matters for security.</strong> The absence of a mixed cell is the
        structural guarantee. Each of the four forbidden generators corresponds to an attempt to use
        a mixed cell that 𝔻₀ does not provide:
      </p>
      <ul>
        <li>
          <strong>G1 (Ambient Effect):</strong> An effect that acts as its own authority witness
          would be a mixed cell — effect grade non-trivial, demand grade non-trivial in the same
          square. 𝔻₀ has no such constructor.
        </li>
        <li>
          <strong>G2 (Epistemic Lift):</strong> Promoting information (horizontal) to authority
          (vertical) is a cross-axis operation. In 𝔻₀, the two axes are orthogonal — no cell bridges
          them without explicit governance data.
        </li>
        <li>
          <strong>G3 (Time Reversal):</strong> The production-demand normal form enforces that cells
          have a definite role. There is no cell that retroactively justifies a prior authorization
          by producing an effect — that would require a mixed cell or a composition path that 𝔻₀
          does not contain.
        </li>
        <li>
          <strong>G4 (Authority Duplication):</strong> Demand grades live exclusively on the
          vertical axis and compose via the monoid <MathInline>{tex`R`}</MathInline>. Duplicating or
          inflating a demand grade requires an operation outside 𝔻₀ — a theorem-specific resource
          proof (RES.CONSUME, RES.RELEASE) provides the governed mechanism.
        </li>
      </ul>
      <WorkedExample
        eyebrow="Real-world scenario"
        title="A microservice that cannot escalate its own privilege"
        accent={ACCENT}
        scenario={
          <p style={{ margin: "0 0 0.5rem" }}>
            A payment microservice produces effects (write a transaction log, emit a receipt) and
            consumes authority (hold a budget-draw capability). In 𝔻₀, these are separate cells:
            producing a log entry is a pure effect square; drawing from the budget is a pure demand
            square.
          </p>
        }
        withoutStructure={
          <>
            Without orthogonality, a service could construct a single cell that says &ldquo;writing
            a receipt <em>is</em> a budget draw&rdquo; — silently coupling its effect output to an
            authority input. The service escalates its own privilege by composition alone.
          </>
        }
        withStructure={
          <>
            In 𝔻₀, such a cell is <em>structurally inexpressible</em>. The only way to connect the
            effect axis to the demand axis is through explicit bridge data — a governed capability
            adoption that appears in the system&apos;s audit trail.
          </>
        }
      />
      <CalloutBox eyebrow="Security guarantee" accent={ACCENT}>
        <p style={{ margin: 0 }}>
          𝔻₀ is the <strong>canonical safe baseline</strong> for every CSK deployment. A deployment
          that adds a cross-axis bridge is introducing <em>extension data</em> — it is not
          discovering a hidden mixed constructor. Every weakening is explicit and governable, and
          the initiality of 𝔻₀ ensures no alternative baseline exists that smuggles in less-governed
          structure.
        </p>
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Differential Security monograph",
      path: "theory/manuscripts/DifferentialSecurity/monograph/03_The_Orthogonal_Embedding.md",
      note: "Source: the monograph's orthogonality claim and canonical safe baseline.",
    },
    {
      label: "Theory note",
      path: "theory/structural/Free-Orthogonal-Graded-Double-Category.md",
      note: "Normative reference: construction, orthogonality, initiality, and security interpretation.",
    },
    {
      label: "Foundation certificate (Agda)",
      path: "agda/Foundation/DifferentialSecurity/FreeOrthogonalGradedDoubleCategory.agda",
      note: "Mechanized witness: zero postulates, Foundation proof path.",
    },
    {
      label: "Pullback-Pushout-BC framework",
      path: "theory/structural/Pullback-Pushout-BC-Adjunction-Framework.md",
      note: "How the free construction relates to seams and Beck-Chevalley adjunctions.",
    },
    {
      label: "Seam theory",
      path: "theory/structural/SEAM-THEORY.md",
      note: "The absent mixed cell as a seam — the structural boundary where governance must supply a bridge.",
    },
  ],
};

// ── Export ───────────────────────────────────────────────────────────

export function FreeOrthogonalGradedDoubleCategoryExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<CellExplorer />}
      crossLinks={CROSS_LINKS}
    />
  );
}
